import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import gsap from 'gsap';

/* ------------------------------------------------------------------ */
/*  Inline SVG icon set — stroke-based, inherits currentColor          */
/* ------------------------------------------------------------------ */
const Icon = ({ name, className = 'w-[18px] h-[18px]' }) => {
  const p = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (name) {
    case 'home':
      return (<svg {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /><path d="M9.5 21v-6h5v6" /></svg>);
    case 'user':
      return (<svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" /></svg>);
    case 'dashboard':
      return (<svg {...p}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="11" width="7" height="10" rx="1.5" /><rect x="3" y="15" width="7" height="6" rx="1.5" /></svg>);
    case 'userPlus':
      return (<svg {...p}><circle cx="9" cy="8" r="3.5" /><path d="M3 21c0-3.5 3-5.5 6-5.5s6 2 6 5.5" /><path d="M19 8v6M16 11h6" /></svg>);
    case 'assign':
      return (<svg {...p}><path d="M8 3h6l4 4v12a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M14 3v4h4" /><path d="m9.5 13 1.8 1.8L15 11" /></svg>);
    case 'doc':
      return (<svg {...p}><path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M14 3v4h4" /><path d="M9 12h6M9 16h6" /></svg>);
    case 'clipboard':
      return (<svg {...p}><rect x="6" y="4" width="12" height="17" rx="2" /><path d="M9 4V3h6v1" /><path d="M9 11h6M9 15h4" /></svg>);
    case 'phone':
      return (<svg {...p}><path d="M6 3h3l1.5 5-2 1.5a13 13 0 0 0 6 6l1.5-2 5 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4 5a2 2 0 0 1 2-2Z" /></svg>);
    case 'gear':
      return (<svg {...p}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></svg>);
    case 'chevron':
      return (<svg {...p}><path d="m6 9 6 6 6-6" /></svg>);
    case 'logout':
      return (<svg {...p}><path d="M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3" /><path d="M10 12H3M6 8l-3 4 3 4" /></svg>);
    case 'bookmark':
      return (<svg {...p}><path d="M6 4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21l-6-4-6 4Z" /></svg>);
    case 'menu':
      return (<svg {...p}><path d="M4 7h16M4 12h16M4 17h16" /></svg>);
    case 'close':
      return (<svg {...p}><path d="m6 6 12 12M18 6 6 18" /></svg>);
    case 'logo':
      return (<svg viewBox="0 0 24 24" fill="none" className={className}><path d="M4 16l5-5 4 4 7-8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /><path d="M15 7h5v5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>);
    default:
      return null;
  }
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Navbar = ({ onLogout, userData: userDataProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isCompany, setIsCompany] = useState(false);
  const [isSubCompany, setIsSubCompany] = useState(false);
  const [ready, setReady] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navRef = useRef(null);
  const profileWrapRef = useRef(null);
  const profilePanelRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const hasEntered = useRef(false);

  // Keep navbar auth state in sync with the app.
  // Prefer the userData prop from App (updates instantly on login/logout);
  // fall back to localStorage on a hard refresh before the prop is ready.
  useEffect(() => {
    const applyUser = (parsedUser) => {
      setIsLoggedIn(true);
      setUserData(parsedUser);
      const role = String(parsedUser?.role || parsedUser?.employee?.role || '').toLowerCase();
      setIsAdmin(Boolean(parsedUser?.is_admin) || role === 'admin');
      setIsEmployee(role === 'employee');
      setIsCompany(parsedUser?.user_type === 'company');
      setIsSubCompany(parsedUser?.user_type === 'sub_company' || role === 'sub_company');
      setReady(true);
    };

    const clearUser = () => {
      setIsLoggedIn(false);
      setUserData(null);
      setIsAdmin(false);
      setIsEmployee(false);
      setIsCompany(false);
      setIsSubCompany(false);
      setReady(true);
    };

    if (userDataProp) {
      applyUser(userDataProp);
      return;
    }

    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token && user) {
      try {
        applyUser(JSON.parse(user));
      } catch {
        clearUser();
      }
    } else {
      clearUser();
    }
  }, [userDataProp]);

  // Subtle scroll-aware glass: stronger blur + shadow once the page moves.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Entrance animation — runs once after auth state settles.
  useLayoutEffect(() => {
    if (!ready || hasEntered.current || prefersReducedMotion()) return;
    hasEntered.current = true;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.from('.js-bar', { y: -24, opacity: 0, duration: 0.55 })
        .from('.js-logo', { y: -10, opacity: 0, scale: 0.9, duration: 0.4 }, '-=0.3')
        .from('.js-pill', { y: -12, opacity: 0, duration: 0.4, stagger: 0.06 }, '-=0.25')
        .from('.js-right', { y: -10, opacity: 0, duration: 0.4 }, '-=0.3');
    }, navRef);
    return () => ctx.revert();
  }, [ready]);

  // Profile dropdown reveal.
  useEffect(() => {
    if (!isProfileMenuOpen || !profilePanelRef.current || prefersReducedMotion()) return;
    gsap.fromTo(
      profilePanelRef.current,
      { opacity: 0, y: -10, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.28, ease: 'power3.out', transformOrigin: 'top right' }
    );
  }, [isProfileMenuOpen]);

  // Mobile sheet reveal + item stagger.
  useEffect(() => {
    if (!isMenuOpen || !mobilePanelRef.current || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.js-sheet', { opacity: 0, y: -14 }, { opacity: 1, y: 0, duration: 0.3, ease: 'power3.out' });
      gsap.fromTo('.js-mobile-item', { opacity: 0, x: -14 }, { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power3.out', delay: 0.05 });
    }, mobilePanelRef);
    return () => ctx.revert();
  }, [isMenuOpen]);

  // Close the profile dropdown when clicking outside it.
  useEffect(() => {
    if (!isProfileMenuOpen) return;
    const onDoc = (e) => {
      if (profileWrapRef.current && !profileWrapRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [isProfileMenuOpen]);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
    setIsProfileMenuOpen(false);
    setIsMenuOpen(false);
  };

  const navLinks = [
    { path: '/dashboard', icon: 'home', label: 'Home' },
    { path: '/saved-jobs', icon: 'bookmark', label: 'Saved' },
    { path: '/profile', icon: 'user', label: 'Profile' },
  ];

  if (isAdmin) {
    navLinks.splice(
      0,
      navLinks.length,
      { path: '/admin-dashboard', icon: 'dashboard', label: 'Admin Home' },
      { path: '/addEmployee', icon: 'userPlus', label: 'Add Employee' },
      { path: '/allLead', icon: 'assign', label: 'Assign Resume' },
      { path: '/admin', icon: 'doc', label: 'All Resumes' }
    );
  } else if (isCompany) {
    navLinks.splice(
      0,
      navLinks.length,
      { path: '/company/dashboard', icon: 'dashboard', label: 'Dashboard' },
      { path: '/company/upload-resumes', icon: 'doc', label: 'Upload' },
      { path: '/company/jobs', icon: 'doc', label: 'Post Job' },
      { path: '/company/uploaded-resumes', icon: 'clipboard', label: 'Resumes' }
    );
  } else if (isSubCompany) {
    navLinks.splice(
      0,
      navLinks.length,
      { path: '/company/client-dashboard', icon: 'dashboard', label: 'Dashboard' }
    );
  } else if (isEmployee) {
    navLinks.splice(
      0,
      navLinks.length,
      ...(userData?.can_view_dashboard === false ? [] : [{ path: '/dashboards', icon: 'dashboard', label: 'Dashboard' }]),
      { path: '/employee-lead-report', icon: 'clipboard', label: 'Assigned' },
      { path: '/follow-up', icon: 'phone', label: 'Follow Up' },
      ...(userData?.can_view_resume_library ? [{ path: '/employee/resume-library', icon: 'doc', label: 'Resumes' }] : []),
      ...(userData?.can_manage_sub_companies ? [{ path: '/employee/sub-companies', icon: 'assign', label: 'Sub Companies' }] : []),
      ...(userData?.can_upload_resumes === false ? [] : [{ path: '/employee/upload-resumes', icon: 'doc', label: 'Upload' }]),
      ...(userData?.can_post_jobs ? [{ path: '/employee/jobs', icon: 'assign', label: 'Post Job' }] : [])
    );
  }

  const displayName =
    userData?.company_name ||
    userData?.sub_company_name ||
    `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() ||
    'User';
  const initial = displayName?.[0]?.toUpperCase() || 'U';

  const roleLabel = isAdmin
    ? 'Administrator'
    : isCompany
    ? 'Company'
    : isSubCompany
    ? 'Sub Company'
    : isEmployee
    ? 'Recruiter'
    : null;

  const desktopPillClass = (link) => {
    const active = location.pathname === link.path;
    const isAdminLink = link.path === '/admin';
    if (active) {
      return isAdminLink
        ? 'text-violet-700 bg-violet-500/10 ring-1 ring-violet-400/30'
        : 'text-indigo-700 bg-indigo-500/10 ring-1 ring-indigo-400/30';
    }
    return 'text-slate-500 hover:text-indigo-600 hover:bg-slate-900/[0.04]';
  };

  return (
    <nav
      ref={navRef}
      className="fixed top-0 inset-x-0 z-50"
    >
      <div
        className={`js-bar transition-all duration-300 ${
          scrolled
            ? 'bg-white/75 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)]'
            : 'bg-white/55 backdrop-blur-md border-b border-white/40'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Logo */}
            <div className="flex items-center gap-5">
              <Link to="/" className="js-logo flex items-center gap-2.5 group">
                <span className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/30 transition-transform duration-300 group-hover:scale-105">
                  <Icon name="logo" className="w-5 h-5" />
                  <span className="absolute inset-0 rounded-xl ring-1 ring-white/20" />
                </span>
                <span className="text-[1.35rem] font-bold tracking-tight bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">
                  Talencar
                </span>
              </Link>

              {/* Admin Badge - Show when on admin pages */}
              {isAdmin && (
                location.pathname.includes('/admin') ||
                location.pathname === '/addEmployee' ||
                location.pathname === '/allLead' ||
                location.pathname === '/viewEmployee'
              ) && (
                <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 bg-violet-500/10 text-violet-700 text-xs font-semibold rounded-full ring-1 ring-violet-400/30">
                  <Icon name="gear" className="w-3.5 h-3.5" />
                  Admin Mode
                </span>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              {isLoggedIn ? (
                <>
                  {/* Nav Links */}
                  <div className="flex items-center gap-1 p-1 rounded-2xl bg-slate-900/[0.03] ring-1 ring-slate-900/[0.04]">
                    {navLinks.map((link) => {
                      const isActive = location.pathname === link.path;
                      return (
                        <Link
                          key={link.path}
                          to={link.path}
                          className={`js-pill relative flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${desktopPillClass(link)}`}
                        >
                          <Icon name={link.icon} />
                          <span>{link.label}</span>
                          {link.path === '/admin' && (
                            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-violet-500 rounded-full" />
                          )}
                          {isActive && (
                            <span className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 h-[2px] w-5 rounded-full bg-current opacity-70" />
                          )}
                        </Link>
                      );
                    })}
                  </div>

                  {/* Profile Menu */}
                  <div className="js-right relative ml-1" ref={profileWrapRef}>
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className="flex items-center gap-2 p-1 pr-2.5 rounded-full hover:bg-slate-900/[0.04] transition-colors"
                    >
                      <span className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-sm font-semibold flex items-center justify-center ring-2 ring-white shadow-sm">
                        {initial}
                      </span>
                      <Icon
                        name="chevron"
                        className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isProfileMenuOpen && (
                      <div
                        ref={profilePanelRef}
                        className="absolute right-0 mt-3 w-72 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-900/10 ring-1 ring-slate-200/70 py-2 z-50 overflow-hidden"
                      >
                        <div className="px-4 py-3.5 border-b border-slate-100">
                          <div className="flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-base font-semibold flex items-center justify-center">
                              {initial}
                            </span>
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900 truncate">
                                {displayName}
                              </p>
                              <p className="text-sm text-slate-500 truncate">
                                {userData?.email}
                              </p>
                            </div>
                          </div>
                          {roleLabel && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 mt-3 bg-indigo-500/10 text-indigo-700 text-xs font-semibold rounded-full ring-1 ring-indigo-400/30">
                              <Icon name={isAdmin ? 'gear' : 'user'} className="w-3.5 h-3.5" />
                              {roleLabel}
                            </span>
                          )}
                        </div>

                        <div className="p-1.5">
                          <Link
                            to="/profile"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-slate-100/80 transition-colors"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <Icon name="user" className="w-[18px] h-[18px] text-slate-400" />
                            View Profile
                          </Link>

                          {isAdmin && (
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-violet-600 hover:bg-violet-500/10 transition-colors"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <Icon name="gear" className="w-[18px] h-[18px]" />
                              Admin Dashboard
                            </Link>
                          )}

                          {isCompany && (
                            <Link
                              to="/company/dashboard"
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-indigo-600 hover:bg-indigo-500/10 transition-colors"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <Icon name="dashboard" className="w-[18px] h-[18px]" />
                              Company Workspace
                            </Link>
                          )}

                          {isSubCompany && (
                            <Link
                              to="/company/client-dashboard"
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-indigo-600 hover:bg-indigo-500/10 transition-colors"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <Icon name="dashboard" className="w-[18px] h-[18px]" />
                              Sub Company Dashboard
                            </Link>
                          )}
                        </div>

                        <div className="border-t border-slate-100 p-1.5">
                          <button
                            onClick={handleLogout}
                            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Icon name="logout" className="w-[18px] h-[18px]" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="js-right flex items-center gap-2">
                  <Link
                    to="/login"
                    className="px-5 py-2 text-sm font-semibold text-slate-600 hover:text-indigo-600 hover:bg-slate-900/[0.04] rounded-xl transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="px-5 py-2 text-sm font-semibold bg-gradient-to-r from-indigo-600 to-violet-500 text-white rounded-xl shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-200"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center justify-center w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-900/[0.05] transition-colors"
                aria-label="Toggle menu"
              >
                <Icon name={isMenuOpen ? 'close' : 'menu'} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div ref={mobilePanelRef} className="md:hidden px-3 pt-2 pb-3">
          <div className="js-sheet bg-white/90 backdrop-blur-xl rounded-2xl ring-1 ring-slate-200/70 shadow-xl shadow-slate-900/10 overflow-hidden">
            {isLoggedIn ? (
              <div className="p-3 space-y-1.5">
                {/* User Info for Mobile */}
                <div className="js-mobile-item flex items-center gap-3 px-3 py-3 bg-gradient-to-br from-indigo-500/[0.06] to-violet-500/[0.06] rounded-xl mb-1">
                  <span className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-lg font-semibold flex items-center justify-center">
                    {initial}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">
                      {displayName}
                    </p>
                    <p className="text-sm text-slate-500 truncate">{userData?.email}</p>
                    {roleLabel && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 mt-1.5 bg-indigo-500/10 text-indigo-700 text-xs font-semibold rounded-full ring-1 ring-indigo-400/30">
                        <Icon name={isAdmin ? 'gear' : 'user'} className="w-3 h-3" />
                        {roleLabel}
                      </span>
                    )}
                  </div>
                </div>

                {navLinks.map((link) => {
                  const isActive = location.pathname === link.path;
                  const isAdminLink = link.path === '/admin';
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`js-mobile-item flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] font-medium transition-colors ${
                        isActive
                          ? isAdminLink
                            ? 'bg-violet-500/10 text-violet-700'
                            : 'bg-indigo-500/10 text-indigo-700'
                          : 'text-slate-600 hover:bg-slate-100/80'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Icon name={link.icon} className="w-5 h-5" />
                      <span>{link.label}</span>
                      {isAdminLink && (
                        <span className="ml-auto text-[11px] font-semibold bg-violet-500/15 text-violet-700 px-2 py-0.5 rounded-full">
                          Admin
                        </span>
                      )}
                    </Link>
                  );
                })}

                <div className="border-t border-slate-100 pt-1.5 mt-1.5">
                  <button
                    onClick={handleLogout}
                    className="js-mobile-item flex w-full items-center gap-3 px-3 py-3 text-red-600 hover:bg-red-50 rounded-xl text-[15px] font-medium transition-colors"
                  >
                    <Icon name="logout" className="w-5 h-5" />
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-3 space-y-2">
                <Link
                  to="/login"
                  className="js-mobile-item block px-4 py-3 text-center font-semibold text-indigo-600 ring-1 ring-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="js-mobile-item block px-4 py-3 text-center font-semibold bg-gradient-to-r from-indigo-600 to-violet-500 text-white rounded-xl shadow-lg shadow-indigo-500/25"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
  
};

export default Navbar;
